
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { CredentialsComponent } from './components/authentication/credentials/credentials.component';
import { LoginComponent } from './components/authentication/login/login.component';
import { StyleGuideComponent } from './components/styleguide/styleguide.component';
import { ForgotUserNameComponent } from './components/authentication/forgotusername/forgotusername.component';
import { CreateAccountComponent } from './components/authentication/createaccount/createaccount.component';
import { MyAccountComponent } from './components/navigation-sidebar/my-account/my-account.component';
import { AccountSummaryComponent } from './components/navigation-sidebar/my-account/account-summary/account-summary.component';
import { MyProfileComponent } from './components/navigation-sidebar/my-account/my-profile/my-profile.component';
import { ChangePasswordComponent } from './components/navigation-sidebar/my-account/change-password/change-password.component';
import { BillingComponent } from './components/navigation-sidebar/billing/billing.component';
import { UserManagementComponent } from './components/navigation-sidebar/my-account/user-management/user-management.component';
import { NavigationSidebarComponent } from './components/navigation-sidebar/navigation-sidebar.component';
import { PaymentMethodComponent } from './components/navigation-sidebar/my-account/payment-method/payment-method.component';
import { MessagesComponent } from './components/navigation-sidebar/messages/messages.component';
import { VerificationComponent } from './components/authentication/verification/verification.component';
import { MyShortCodesComponent } from './components/navigation-sidebar/my-short-codes/my-short-codes.component';
import { ShortCodeListComponent } from './components/navigation-sidebar/my-short-codes/short-code-list/short-code-list.component';
import { NeedsActionComponent } from './components/navigation-sidebar/my-short-codes/needs-action/needs-action.component';
import { CodeTransfersComponent } from './components/navigation-sidebar/my-short-codes/code-transfers/code-transfers.component';
import { MonthlyBillingComponent } from './components/navigation-sidebar/my-short-codes/monthly-billing/monthly-billing.component';
import { IndustryReportsComponent } from './components/navigation-sidebar/industry-reports/industry-reports.component';
import { BrandIdentificationComponent } from './components/navigation-sidebar/industry-reports/brand-identification/brand-identification.component';
import { ComplaintClassificationComponent } from './components/navigation-sidebar/industry-reports/complaint-classification/complaint-classification.component';
import { InMarketMonitoringComponent } from './components/navigation-sidebar/industry-reports/in-market-monitoring/in-market-monitoring.component';
import { MonthEndReportComponent } from './components/navigation-sidebar/industry-reports/month-end-report/month-end-report.component';
import { VettingInsightsComponent } from './components/navigation-sidebar/industry-reports/vetting-insights/vetting-insights.component';

import { LoginGuard, ScrAuthorizationGuard } from './services/scr-authorization.guard';
import { ShortCodesRole } from './models/constants';

import { OrderShortCodeComponent } from './components/navigation-sidebar/order-short-code/order-short-code.component';
import { CartComponent } from './components/navigation-sidebar/order-short-code/cart/cart.component';
import { ApplicationComponent } from './components/navigation-sidebar/order-short-code/application/application.component';

import { CodeManagementComponent } from './components/navigation-sidebar/code-management/code-management.component';
import { RegistryReportsComponent } from './components/navigation-sidebar/registry-reports/registry-reports.component';
import { ReportsComponent } from './components/navigation-sidebar/reports/reports.component';
import { ViewOrdersComponent } from './components/navigation-sidebar/billing/view-orders/view-orders.component';
import { NeedsPaymentComponent } from './components/navigation-sidebar/billing/needs-payment/needs-payment.component';
import { OrderDetailComponent } from './components/navigation-sidebar/billing/order-detail/order-detail.component';
import { InvoiceComponent } from './components/navigation-sidebar/billing/invoice/invoice.component';
import { CodeLookupComponent } from './components/navigation-sidebar/code-management/code-lookup/code-lookup.component';
import { CodeHistoryComponent } from './components/navigation-sidebar/code-management/code-history/code-history.component';
import { ReceiptComponent } from './components/navigation-sidebar/my-short-codes/receipt/receipt.component';
import { ScrCanDeactivateGuard } from './services/scr-candeactivate.guard';
import { PaymentComponent } from './components/navigation-sidebar/order-short-code/payment/payment.component';
import { ApplicationThankYouComponent } from './components/navigation-sidebar/order-short-code/application-thank-you/application-thank-you.component';
import { MonthlyStatementComponent } from './components/navigation-sidebar/my-short-codes/monthly-statement/monthly-statement.component';
import { EditDetailsComponent } from './components/navigation-sidebar/my-short-codes/edit-details/edit-details.component';
import { EditBillingDetailsComponent } from './components/navigation-sidebar/my-short-codes/edit-billing-details/edit-billing-details.component';
import { EmailRedirectorGuard } from './shared/email.redirector.guard';
import { UnauthorizedComponent } from './components/navigation-sidebar/unauthorized/unauthorized.component';
import { EditAccountComponent } from './components/navigation-sidebar/my-account/edit-account/edit-account.component';
import { CheckoutPaymentComponent } from './components/navigation-sidebar/checkout';
import { ThankYouComponent } from './components/navigation-sidebar/thank-you/thank-you.component';

import {
  LinkedAccountsComponent,
  LinkedAccountsNeedsActionComponent,
  LinkedAccountsAllCodesComponent,
  LinkedAccountsListComponent
} from './components/navigation-sidebar/linked-accounts';
import { SuspiciousActivityComponent } from './components/navigation-sidebar/suspicious-activity/suspicious-activity.component';
import { CarrierReportedActivitiesComponent } from './components/navigation-sidebar/suspicious-activity/carrier-reported-activities/carrier-reported-activities.component';
import { ReportSuspiciousActivityComponent } from './components/navigation-sidebar/suspicious-activity/report-suspicious-activity/report-suspicious-activity.component';
import { LandingComponent } from './components/landing/landing.component';
import { PartnerListComponent } from './components/navigation-sidebar/partner-library/partner-list/partner-list.component';
import { PartnerLibraryComponent } from './components/navigation-sidebar/partner-library/partner-library.component';


const routes: Routes = [
  {
    path: 'landing',
    component: LandingComponent,
  },
  {
    path: 'primeng',
    component: StyleGuideComponent
  },
  {
    path: 'sec/login',
    data: {
      reloadAfterSignOut: false
    },
    component: LoginComponent,
  },
  {
    path: 'sec/logout',
    component: LoginComponent
  },
  {
    path: 'sec/inactivitylogout',
    component: LoginComponent,
    data: { unattended: true }
  },
  {
    path: 'sec/linkedsessiontimeout',
    component: LoginComponent,
    data: { unattended: true }
  },
  {
    path: 'sec/forgotpassword',
    component: CredentialsComponent
  },
  {
    path: 'changepassword/:token',
    component: CredentialsComponent
  },
  {
    path: 'createcredentials/:token',
    component: CredentialsComponent
  },
  {
    path: 'sec/forgotusername',
    component: ForgotUserNameComponent
  },
  {
    path: 'sec/createaccount',
    component: CreateAccountComponent
  },
  {
    path: 'sec/verify/:nonce',
    component: VerificationComponent
  },
  {
    path: 'sec/verify',
    component: VerificationComponent
  },
  {
    path: 'dashboard',
    component: NavigationSidebarComponent,
    canActivate: [LoginGuard],
    children: [
      {
        path: 'my-shortcodes', component: MyShortCodesComponent, outlet: 'navbar-tab',
        canActivate: [LoginGuard, ScrAuthorizationGuard],
        data: { groups: [ShortCodesRole.Registrant, ShortCodesRole.CarrierRegistrant, ShortCodesRole.Carrier] },
        children: [
          { path: 'shortcode-list', component: ShortCodeListComponent, outlet: 'my-shortcode-tab', canActivate: [LoginGuard], canDeactivate: [ScrCanDeactivateGuard] },
          { path: 'shortcode-needs-action', component: NeedsActionComponent, outlet: 'my-shortcode-tab', canActivate: [LoginGuard] },
          { path: 'shortcode-code-transfers', component: CodeTransfersComponent, outlet: 'my-shortcode-tab', canActivate: [LoginGuard] },
          { path: 'shortcode-monthly-billing', component: MonthlyBillingComponent, outlet: 'my-shortcode-tab', canActivate: [LoginGuard] },
          { path: 'order/:orderId', component: OrderDetailComponent, outlet: 'my-shortcode-tab', canActivate: [LoginGuard] },
          { path: 'invoice/:orderId', component: InvoiceComponent, outlet: 'my-shortcode-tab', canActivate: [LoginGuard] },
          { path: 'invoice/:orderId/:paymentId', component: InvoiceComponent, outlet: 'my-shortcode-tab', canActivate: [LoginGuard] },
          { path: 'receipt/:orderId', component: ReceiptComponent, outlet: 'my-shortcode-tab', canActivate: [LoginGuard] },
          { path: 'receipt/:orderId/:scId', component: ReceiptComponent, outlet: 'my-shortcode-tab', canActivate: [LoginGuard] },
          { path: 'multiPaymentReceipt/:paymentId', component: ReceiptComponent, outlet: 'my-shortcode-tab', canActivate: [LoginGuard] },
          { path: 'statement/:orderId', component: MonthlyStatementComponent, outlet: 'my-shortcode-tab', canActivate: [LoginGuard] },
          { path: 'details/:scId', component: EditDetailsComponent, outlet: 'my-shortcode-tab', canActivate: [LoginGuard] },
          { path: 'billingDetails/:scId', component: EditBillingDetailsComponent, outlet: 'my-shortcode-tab', canActivate: [LoginGuard] },
        ]
      },
      {
        path: 'my-account', component: MyAccountComponent, outlet: 'navbar-tab', canActivate: [LoginGuard],
        children: [
          { path: 'account-summary', component: AccountSummaryComponent, outlet: 'my-account-tab', canActivate: [LoginGuard] },
          { path: 'edit-account', component: EditAccountComponent, outlet: 'my-account-tab', data: { mode: 'edit' }, canActivate: [LoginGuard] },
          { path: 'my-profile', component: MyProfileComponent, outlet: 'my-account-tab', canActivate: [LoginGuard] },
          { path: 'change-password', component: ChangePasswordComponent, outlet: 'my-account-tab', canActivate: [LoginGuard] },
          {
            path: 'user-management', component: UserManagementComponent, outlet: 'my-account-tab',
            canActivate: [LoginGuard, ScrAuthorizationGuard], data: { groups: [ShortCodesRole.RegistrantAdmin, ShortCodesRole.Carrier] }
          },
          { path: 'payment-method', component: PaymentMethodComponent, outlet: 'my-account-tab', canActivate: [LoginGuard] }
        ]
      },
      {
        path: 'linked-accounts', component: LinkedAccountsComponent, outlet: 'navbar-tab', canActivate: [LoginGuard],
        children: [
          { path: 'all-short-codes', component: LinkedAccountsAllCodesComponent, outlet: 'linked-accounts-tab', canActivate: [LoginGuard] },
          { path: 'needs-action', component: LinkedAccountsNeedsActionComponent, outlet: 'linked-accounts-tab', canActivate: [LoginGuard] },
          { path: 'linked-accounts', component: LinkedAccountsListComponent, outlet: 'linked-accounts-tab', canActivate: [LoginGuard] }
        ]
      },
      {
        path: 'partner-library', component: PartnerLibraryComponent, outlet: 'navbar-tab', canActivate: [LoginGuard],
        children: [
          { path: 'my-brands', component: PartnerListComponent, outlet: 'partner-library-tab', data: { partnerType: 'Brand' }, canActivate: [LoginGuard] },
          { path: 'my-content-providers', component: PartnerListComponent, outlet: 'partner-library-tab', data: { partnerType: 'ContentProvider' }, canActivate: [LoginGuard] },
        ]
      },
      {
        path: 'industry-reports', component: IndustryReportsComponent, outlet: 'navbar-tab', canActivate: [LoginGuard, ScrAuthorizationGuard],
        data: { groups: [ShortCodesRole.VettingMonitoring] },
        children: [
          { path: 'brand-identification', component: BrandIdentificationComponent, outlet: 'industry-reports-tab', canActivate: [LoginGuard] },
          { path: 'in-market-monitoring', component: InMarketMonitoringComponent, outlet: 'industry-reports-tab', canActivate: [LoginGuard] },
          { path: 'month-end-report', component: MonthEndReportComponent, outlet: 'industry-reports-tab', canActivate: [LoginGuard] },
          { path: 'complaint-classification', component: ComplaintClassificationComponent, outlet: 'industry-reports-tab', canActivate: [LoginGuard] },
          { path: 'vetting-insights', component: VettingInsightsComponent, outlet: 'industry-reports-tab', canActivate: [LoginGuard] }

        ]
      },
      { path: 'messages', component: MessagesComponent, outlet: 'navbar-tab', canActivate: [LoginGuard] },
      {
        path: 'billing', component: BillingComponent, outlet: 'navbar-tab',
        canActivate: [LoginGuard, ScrAuthorizationGuard],
        data: { groups: [ShortCodesRole.Registrant, ShortCodesRole.CarrierRegistrant, ShortCodesRole.Carrier] },
        children: [
          { path: 'view-orders', component: ViewOrdersComponent, outlet: 'billing-tab', canActivate: [LoginGuard] },
          { path: 'needs-payment', component: NeedsPaymentComponent, outlet: 'billing-tab', canActivate: [LoginGuard] },
          { path: 'monthly-invoice', component: MonthlyBillingComponent, outlet: 'billing-tab', canActivate: [LoginGuard] },
          { path: 'order/:orderId', component: OrderDetailComponent, outlet: 'billing-tab', canActivate: [LoginGuard] },
          { path: 'invoice/:orderId', component: InvoiceComponent, outlet: 'billing-tab', canActivate: [LoginGuard] },
          { path: 'receipt/:orderId', component: ReceiptComponent, outlet: 'billing-tab', canActivate: [LoginGuard] },

        ]
      },
      {
        path: 'order-code', component: OrderShortCodeComponent, outlet: 'navbar-tab', canDeactivate: [ScrCanDeactivateGuard], canActivate: [LoginGuard, ScrAuthorizationGuard],
        data: { outlet: 'order-code-tab', defaultChildRoute: 'cart', groups: [ShortCodesRole.Registrant, ShortCodesRole.CarrierRegistrant, ShortCodesRole.Carrier] },
        children: [
          { path: 'cart', component: CartComponent, outlet: 'order-code-tab', data: { disableAppTab: true }, canActivate: [LoginGuard] },
          { path: 'application', component: ApplicationComponent, outlet: 'order-code-tab', data: { disableAppTab: true }, canActivate: [LoginGuard] },
          { path: 'payment', component: PaymentComponent, outlet: 'order-code-tab', data: { disableAppTab: false }, canActivate: [LoginGuard], canDeactivate: [ScrCanDeactivateGuard] }
        ]
      },
      { path: 'thank-you/:orderId', component: ApplicationThankYouComponent, outlet: 'navbar-tab', canActivate: [LoginGuard] },
      {
        path: 'code-management', component: CodeManagementComponent, outlet: 'navbar-tab', canActivate: [LoginGuard, ScrAuthorizationGuard],
        data: { outlet: 'code-management-tab', defaultChildRoute: 'code-lookup', groups: [ShortCodesRole.Carrier] },
        children: [
          { path: 'code-lookup', component: CodeLookupComponent, outlet: 'code-management-tab', canActivate: [LoginGuard] },
          { path: 'code-history', component: CodeHistoryComponent, outlet: 'code-management-tab', canActivate: [LoginGuard] }
        ]
      },
      {
        path: 'suspicious-activity', component: SuspiciousActivityComponent, outlet: 'navbar-tab', canActivate: [LoginGuard, ScrAuthorizationGuard],
        data: { outlet: 'suspicious-activity-tab', defaultChildRoute: 'carrier-reported-activities', groups: [ShortCodesRole.Carrier] },
        children: [
          { path: 'carrier-reported-activities', component: CarrierReportedActivitiesComponent, outlet: 'suspicious-activity-tab', canActivate: [LoginGuard] },
          { path: 'report-suspicious-activity', component: ReportSuspiciousActivityComponent, outlet: 'suspicious-activity-tab', canActivate: [LoginGuard] }
        ]
      },
      {
        path: 'registry-reports', component: RegistryReportsComponent, outlet: 'navbar-tab',
        canActivate: [LoginGuard, ScrAuthorizationGuard], data: { groups: [ShortCodesRole.Carrier] }
      },

      {
        path: 'payment', component: CheckoutPaymentComponent, outlet: 'navbar-tab',
        canActivate: [LoginGuard, ScrAuthorizationGuard], data: { groups: [ShortCodesRole.Registrant] }
      },

      {
        path: 'thank-you', component: ThankYouComponent, outlet: 'navbar-tab', canActivate: [LoginGuard]
      },


      {
        path: 'reports', component: ReportsComponent, outlet: 'navbar-tab', canActivate: [LoginGuard, ScrAuthorizationGuard],
        data: { groups: [ShortCodesRole.Registrant, ShortCodesRole.CarrierRegistrant, ShortCodesRole.Carrier] }
      },


      {
        path: 'unauthorized', component: UnauthorizedComponent, outlet: 'navbar-tab'
      },

        // if user signs in as registrant in SD web-app, they end up here.
            { path: '**', component: UnauthorizedComponent, outlet: 'navbar-tab', data: { compareCredentialsToApp: true } },
      
    ]
  },
  {
    path: '**',
    component: LandingComponent,
    canActivate: [EmailRedirectorGuard]
  }

];

@NgModule({
  imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'top', enableTracing: false })],
  exports: [RouterModule],
  providers: [ScrCanDeactivateGuard]
})
export class AppRoutingModule { }
